import { useRef, useState } from 'react';
import SVG from 'react-inlinesvg';
import { useNavigate } from 'react-router-dom';

import { PAYMENT_METHODS } from '@autopass-web/domain';
import {
  Alert,
  Button,
  ButtonDialog,
  Carousel,
  FormInput,
} from '@autopass-web/mochi-component';
import {
  axiosAutoPassErrorConverter,
  zodErrorConverter,
} from '@autopass-web/mochi-utils';

import { filterAndSortPromotions } from './filter-and-sort-promotions';
import styles from './index.module.scss';
import { parsePaymentMethodString } from './parse-payment-method-string';
import { sortByPlateNumberAndCheckedInTime } from './sort-by-plate-number-and-checkedin-time';

import type { AutopassAxiosError } from '@autopass-web/mochi-utils';
import type { VehicleType } from '@autopass-web/types';

import { PageContainer } from '#/guest-aps/components/page-container';
import { PinIcon } from '#/guest-aps/components/pin-icon';
import { RadioGroup } from '#/guest-aps/components/radio-group';
import { ERROR_CODES, PATH, vehicle } from '#/guest-aps/config';
import { useAmplitudeSearchPlateNumberHintPageView } from '#/guest-aps/hooks/use-amplitude-search-plate-number-hint-page-view';
import { useAmplitudeSearchPlateNumberPageView } from '#/guest-aps/hooks/use-amplitude-search-plate-number-page-view';
import { useErrorMessageHeight } from '#/guest-aps/hooks/use-error-message-height';
import amplitude from '#/guest-aps/services/amplitude';
import {
  localStorageController,
  sessionStorageController,
} from '#/guest-aps/services/storages';
import { useBulletinBoards } from '#/guest-aps/services/use-bulletin-boards';
import { useParkingRecordBills } from '#/guest-aps/services/use-parking-record-bills';
import { createParkinglotCodeReplacer } from '#/guest-aps/utils/bulletin-replacer';
import { createCarouselPromotionList } from '#/guest-aps/utils/create-carousel-promotion-list';
import { parseApsStyles } from '#/guest-aps/utils/parse-aps-styles';
import { getPlateNumberValidation } from '#/guest-aps/utils/validation';

/**
 * 1. Store entire parkinglot and apsPartnerTemplate inside sessionStorage v
 * 2. Error Handling
 * 3. Amplitude Event places correctly
 * 4. Rethink storage strategy v
 */

// EASY_WALLET is not supported
const DEFAULT_PAYMENT_METHODS = Object.values(PAYMENT_METHODS).filter(
  (method) => method !== PAYMENT_METHODS.EASY_WALLET,
);

export const SearchPlateNumberPage = () => {
  const parkinglot = sessionStorageController.getParkinglot();
  const apsPartnerTemplate = sessionStorageController.getApsPartner();

  const availableVehicleTypes = Object.keys(vehicle).filter(
    (x) => (parkinglot?.vehicle_types ?? []).indexOf(x as VehicleType) !== -1,
  );
  const defaultVehicleType =
    sessionStorageController.getVehicleType() ?? availableVehicleTypes[0];

  const navigate = useNavigate();
  const alertRef = useRef<HTMLDivElement>(null);

  const [selectedVehicleType, setSelectedVehicleType] =
    useState<string>(defaultVehicleType);
  const [plateNumberFuzzy, setPlateNumberFuzzy] = useState<string>(
    localStorageController.getPlateNumberFuzzy() ?? '',
  );

  const [plateNumberErrorMessage, setPlateNumberErrorMessage] =
    useState<string>('');
  const [alertErrorMessage, setAlertErrorMessage] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useAmplitudeSearchPlateNumberPageView();
  useAmplitudeSearchPlateNumberHintPageView(isOpen);

  const { refetchParkingRecordBills, isGetParkingRecordBillsLoading } =
    useParkingRecordBills({
      plateNumber: plateNumberFuzzy,
      parkinglotId: parkinglot.id,
      vehicleType: selectedVehicleType,
    });

  const { bulletinBoards } = useBulletinBoards({
    codes: 'aps_promotion, service_maintenance_announcement',
  });
  const errorMessageHeight = useErrorMessageHeight(
    alertRef,
    !!alertErrorMessage,
  );

  const handleRadioChange = (data: string) => {
    amplitude.searchPlateNumberVehicleTypeOnClick(data);
    setSelectedVehicleType(data);
  };

  const handleSubmit = async () => {
    const plateNumberValidation = getPlateNumberValidation();

    // parse plateNumber to check if it's valid
    try {
      plateNumberValidation.parse(plateNumberFuzzy);
    } catch (error) {
      const zodResult = zodErrorConverter(error);
      const errorMessage = zodResult[0]?.message;

      amplitude.searchPlateNumberErrorPageView(error);
      setPlateNumberErrorMessage(errorMessage);
      return;
    }

    // Fetch parking record bills
    const { data, error } = await refetchParkingRecordBills();

    if (error) {
      amplitude.searchPlateNumberErrorPageView(error);

      const errorObj = axiosAutoPassErrorConverter(error as AutopassAxiosError);

      if (errorObj == null) {
        setAlertErrorMessage('未知錯誤');
        return;
      }

      if (errorObj.metadata.status >= 500) {
        setAlertErrorMessage(errorObj.message);
      } else {
        setPlateNumberErrorMessage(errorObj.message);
      }

      return;
    }

    const parkingRecordBills = data?.data?.data ?? [];

    // If there is no any parking record bills, show error message and return
    if (parkingRecordBills.length === 0) {
      amplitude.searchPlateNumberErrorPageView({
        reason: '查詢不到相近車牌號碼',
        code: ERROR_CODES.NO_SIMILAR_LICENSE_PLATE,
      });

      const errorMessage = apsPartnerTemplate
        ? '查詢不到相近車牌號碼，請重新輸入。或請聯絡客服中心：0809-008-924'
        : '查詢不到相近車牌號碼，請重新輸入。或找管理員協助繳費出場。';

      setPlateNumberErrorMessage(errorMessage);
      return;
    }

    // Sort parking record bills by plate number and checked in time
    // Then, split the list into matched and unmatched list based on the input plate number
    const sortedList = parkingRecordBills.sort(
      sortByPlateNumberAndCheckedInTime,
    );

    const matchedResult = sortedList.filter((bill) =>
      bill.plate_number.includes(plateNumberFuzzy),
    );
    const unmatchedResult = sortedList.filter(
      (bill) => !bill.plate_number.includes(plateNumberFuzzy),
    );

    // Put matchedResult in front of unmatchedResult
    const sortedResult = matchedResult.concat(unmatchedResult);

    localStorageController.savePlateNumberFuzzy(plateNumberFuzzy);
    // Store the result into sessionStorage
    sessionStorageController.saveVehicleType(selectedVehicleType);
    sessionStorageController.savePlateNumberSearchResult(sortedResult);

    navigate(PATH.SELECT_PLATE_NUMBER);
  };

  const promotions = filterAndSortPromotions(
    (bulletinBoards?.aps_promotion ?? []).map(
      createParkinglotCodeReplacer(parkinglot),
    ),
  );

  const announcements = bulletinBoards?.service_maintenance_announcement ?? [];

  const { appBackgroundStyleObj, appTextStyleObj, appTextColor } =
    parseApsStyles(apsPartnerTemplate);

  return (
    <>
      <ButtonDialog
        onDismiss={() => setIsOpen(false)}
        show={isOpen}
        title="輸入範例"
        message="若車牌為 J8Q-520，請輸入連號數字「520」後送出。"
        buttonList={[
          {
            beLoading: false,
            theme: 'positive',
            content: '返回',
            onClick: () => setIsOpen(false),
          },
        ]}
      />
      <Alert
        theme="warning"
        ref={alertRef}
        className={styles.alert}
        type="full"
        onClose={() => setAlertErrorMessage('')}
        show={!!alertErrorMessage}
      >
        {alertErrorMessage}
      </Alert>
      <PageContainer>
        <div className={styles.top} style={{ marginTop: errorMessageHeight }}>
          <div className={styles.background} style={appBackgroundStyleObj} />
          <div className={styles.parkinglot} style={appTextStyleObj}>
            <PinIcon textColor={appTextColor} />
            {parkinglot.name}
          </div>
          <div className={styles.card}>
            <div className={styles.title}>
              請輸入車牌數字
              <SVG
                src="/assets/info-outlined.svg"
                onClick={() => {
                  setIsOpen(true);
                  amplitude.searchPlateNumberHintOnClick();
                }}
              />
            </div>

            <FormInput
              appearance="grey"
              onFocus={() => amplitude.searchPlateNumberInputOnClick()}
              placeholder="請輸入車牌 3-4 碼連號數字"
              inputMode="numeric"
              value={plateNumberFuzzy}
              onTextChange={(value) => {
                setPlateNumberFuzzy(value);
                setPlateNumberErrorMessage('');
              }}
              maxLength={4}
              errorMessage={plateNumberErrorMessage}
              onClear={() => {
                setPlateNumberFuzzy('');
                setPlateNumberErrorMessage('');
              }}
            />

            <div className={styles['car-type']}>
              <RadioGroup
                list={availableVehicleTypes.map((v) => ({
                  id: v,
                  title: vehicle[v],
                }))}
                value={selectedVehicleType}
                onChange={handleRadioChange}
                name="car-type"
              />
            </div>
            <Button
              style={{ ...appBackgroundStyleObj, ...appTextStyleObj }}
              loading={isGetParkingRecordBillsLoading}
              onClick={() => {
                amplitude.searchPlateNumberButtonOnClick(
                  plateNumberFuzzy,
                  selectedVehicleType,
                );
                handleSubmit();
              }}
              size="full"
              type="solid-round"
            >
              查詢車牌
            </Button>
          </div>
          <ul className={styles.hint}>
            {announcements.map((item) => (
              <li key={item.id} style={{ color: item.attributes.color }}>
                {item.attributes.value}
              </li>
            ))}
            <li>
              目前支援{' '}
              {parsePaymentMethodString(
                apsPartnerTemplate?.attributes.support_payments ??
                  DEFAULT_PAYMENT_METHODS,
              )}{' '}
              付款方式。
            </li>
            <li>
              發票開立可選擇「手機條碼載具」、「統編發票」、「發票捐贈」、「信箱載具」設定。
            </li>
          </ul>
        </div>
        <div className="px-5">
          <Carousel
            loading={promotions == null}
            items={createCarouselPromotionList(
              {
                promotions,
                apsPartnerTemplate,
              },
              {
                onClickFactory: (promotion) => () => {
                  amplitude.searchPlateNumberPromotionOnClick(promotion.id);
                },
              },
            )}
            indicator={false}
          />
        </div>
      </PageContainer>
    </>
  );
};
